import { useMutation } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { showMutationErrorToast } from '@/lib/toast';
import { useAuthQuery } from './use-auth-query';
import { useBatchedPaginatedQuery } from './use-batched-paginated-query';
import type { BatchFetcherOpts } from './use-batched-paginated-query';

export function useInspection(id: string) {
  return useAuthQuery({
    queryKey: queryKeys.inspections.detail(id),
    queryFn: () => api.getReport(id),
    staleTime: 1000 * 30,
    enabled: !!id,
  });
}

/**
 * Requests a server-generated report PDF and opens the returned presigned
 * download URL. Replaces the old client-side browser-print export.
 */
export function useReportPdf() {
  return useMutation({
    mutationFn: (inspectionId: string) => api.getReportPdfUrl(inspectionId),
    onSuccess: ({ url }) => {
      // Trigger the download via a transient anchor rather than a top-level
      // navigation. window.location.assign() starts a main-frame navigation that
      // S3 turns into a download (Content-Disposition: attachment) — the file
      // saves, but the navigation never "completes", so the tab is left spinning
      // (visible pause/loading icon in Chromium browsers). An anchor click routes
      // straight into the download subsystem and leaves the tab untouched.
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.rel = 'noopener';
      anchor.style.display = 'none';
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
    },
    onError: (error) => showMutationErrorToast(error, 'Failed to generate PDF. Please try again.'),
  });
}

export function useTaskInspection(taskId: string, opts: { enabled?: boolean } = {}) {
  return useAuthQuery({
    queryKey: queryKeys.inspections.byTask(taskId),
    queryFn: () => api.getTaskInspection(taskId),
    staleTime: 1000 * 60 * 5,
    enabled: (opts.enabled ?? true) && !!taskId,
  });
}

interface ReviewQueueOpts {
  status: 'pending_review' | 'reviewed';
  viewPage: number;
  viewPageSize?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  enabled?: boolean;
  search?: string;
}

export function useInspections(opts: ReviewQueueOpts) {
  const viewPageSize = opts.viewPageSize ?? 10;
  return useBatchedPaginatedQuery(
    queryKeys.inspections.all(),
    (fetchOpts: BatchFetcherOpts) =>
      api.getReviewQueue({
        status: fetchOpts.status as 'pending_review' | 'reviewed',
        page: fetchOpts.page,
        limit: fetchOpts.limit,
        sortBy: fetchOpts.sortBy as string | undefined,
        sortOrder: fetchOpts.sortOrder as 'asc' | 'desc' | undefined,
        search: fetchOpts.search as string | undefined,
      }),
    {
      viewPage: opts.viewPage,
      viewPageSize,
      sortBy: opts.sortBy,
      sortOrder: opts.sortOrder,
      filters: { status: opts.status, search: opts.search },
      staleTime: 1000 * 30,
      enabled: opts.enabled,
    },
  );
}

export function usePendingReviews(opts: Omit<ReviewQueueOpts, 'status'> = { viewPage: 1 }) {
  return useInspections({ ...opts, status: 'pending_review' });
}

export function useReviewedInspections(opts: Omit<ReviewQueueOpts, 'status'> = { viewPage: 1 }) {
  return useInspections({ ...opts, status: 'reviewed' });
}
